import type { Options, WebSocketEventMap } from "partysocket/ws";
import type { OpencodeEvent } from "schemas/agentTools/opencode";

import PartySocket from "partysocket/ws";
import { parseJsonOrNull } from "replo-utils/lib/json";
import {
  REPLO_BEARER_WS_PROTOCOL_PREFIX,
  REPLO_BEDROCK_WS_PROTOCOL,
} from "schemas/authTokens";
import {
  WS_CLOSE_AUTH_FAILED,
  WS_CLOSE_HEARTBEAT_TIMEOUT,
  WS_CLOSE_IDENTITY_MISMATCH,
  WS_CLOSE_PAGE_HIDDEN,
} from "schemas/wsCloseCodes";
import { v4 as uuidv4 } from "uuid";

import {
  BedrockApiRequestError,
  BedrockSessionInvalidatedError,
  REALTIME_RPC_REJECTED_CODE,
} from "./errors";

export type CoordinatorOpencodeEventEnvelope = {
  type: "opencode-event";
  payload: OpencodeEvent;
  sessionId?: string;
};

export type RealtimeDialFailureReason =
  | "token-mint-failed"
  | "url-build-failed"
  | "socket-error-before-open"
  | "connect-timeout";

export type RealtimeDialFailure = {
  reason: RealtimeDialFailureReason;
  message: string;
};

// NOTE (Andrew, 2026-07-24): The token resolves before the socket is constructed, so these legs are additive, not nested.
export type RealtimeDialTimings = {
  /** Time in `resolveAuthToken`: a full round trip on a page load's first dial, ~0 on reconnects that hit the caller's cache. */
  tokenMintMs: number;
  /** Auth resolution to `open`: TCP/TLS, the upgrade, and the coordinator's `onConnect`. */
  wsHandshakeMs: number;
  /** Joins this open to the origin probe measured alongside the same dial. */
  dialId: string;
};

export interface RealtimeConnectionOptions {
  routing?: {
    devAlias?: string | null;
    initialSnapshotRef?: string | null;
  };
  autoReconnect?: boolean;
  onEvent?: (envelope: CoordinatorOpencodeEventEnvelope) => void;
  onRawMessage?: (raw: unknown) => void;
  onOpen?: (dial: RealtimeDialTimings) => void;
  onClose?: (
    event: CloseEvent | undefined,
    context: { willReconnect: boolean },
  ) => void;
  onError?: (error: Event) => void;
  // (Andrew, 2026-07-05, REPL-28639): Fires when a dial never reaches open — the failure mode `onClose` alone can't attribute (e.g. the mint-401 doomed-dial loop).
  onDialFailed?: (failure: RealtimeDialFailure) => void;
  // (Andrew, 2026-07-28): Fires when the handshake clock starts, so a consumer can time a control request against the same network conditions.
  onHandshakeStart?: (dialId: string) => void;
}

export interface ResolvedAuthToken {
  token?: string;
  projectId?: string;
  userId?: string;
}

const REALTIME_CONNECTION_TIMEOUT_MS = 30_000;
const REALTIME_BASE_RECONNECT_DELAY_MS = 500;
const REALTIME_MAX_RECONNECT_DELAY_MS = 10_000;
const REALTIME_STABLE_CONNECTION_MS = 30_000;
// NOTE (Andrew, 2026-06-03, REPL-27669): A 4001 close means the server rejected
// the token. We force-refresh and retry a few times to ride out an
// expired/rotated token, then treat it as terminal rather than hammering the
// auth server forever on a revoked credential.
const REALTIME_MAX_AUTH_RETRIES = 3;
// Matches the sandbox supervisor's 5s heartbeat cadence.
const REALTIME_HEARTBEAT_INTERVAL_MS = 5_000;
// (Andrew, 2026-07-01, REPL-28678): One silent interval is usually latency jitter, not death — tolerate this many before giving up.
const REALTIME_HEARTBEAT_MAX_SILENT_TICKS = 1;
// NOTE (Ryan, 2026-09-28, REPL-32860): The coordinator's runtime answers a ping without waking it, so a socket that never gets a pong is on an instance that is never delivered frames. Other frames prove nothing: the coordinator pushes several on every connect over the outbound path, which keeps working. These are the exact bytes of its `setWebSocketAutoResponse` reply.
const PONG_FRAME = JSON.stringify({ type: "pong" });
// NOTE (Andrew, 2026-05-22, REPL-27376): Bound every RPC so a wedged DO call
// or silently-dead socket can't freeze callers forever.
const REALTIME_RPC_TIMEOUT_MS = 60_000;
export const REALTIME_RPC_TIMEOUT_CODE = "REALTIME_RPC_TIMEOUT";
export const REALTIME_CONNECTION_CLOSED_CODE = "REALTIME_CONNECTION_CLOSED";
// Single source of truth so every REALTIME_CONNECTION_CLOSED rejection reads the
// same in logs, whether the socket closed on us or a consumer tore it down.
const REALTIME_CONNECTION_CLOSED_MESSAGE = "WebSocket connection closed";

// NOTE (Yuxin, 2026-07-27): these leave the RPC outcome unknown — the server may have completed the call, so don't read them as a server "no".
export function isRealtimeTransportError(error: unknown): boolean {
  if (error instanceof BedrockApiRequestError) {
    return (
      error.code === REALTIME_CONNECTION_CLOSED_CODE ||
      error.code === REALTIME_RPC_TIMEOUT_CODE
    );
  }
  // Wrappers like CloudflareAgentCoordinatorRpcError keep it as `cause`.
  return error instanceof Error && isRealtimeTransportError(error.cause);
}
// (Andrew, 2026-07-05, REPL-28639): Error text only, size-bounded so a giant response body can't bloat the telemetry event.
const DIAL_FAILURE_MESSAGE_MAX_LENGTH = 200;
// (Andrew, 2026-07-29): Bounds the pre-attach inbox; a dropped prefix self-heals because part events are id-keyed upserts and history hydrates from Postgres.
const PRE_ATTACH_INBOX_LIMIT = 200;

export class RealtimeConnection {
  private socket: InstanceType<typeof PartySocket> | null = null;
  private forceRefreshTokenOnNextConnect = false;
  private authFailureCount = 0;
  private connectionAuthPromise: Promise<ResolvedAuthToken> | null = null;
  private connectionTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private lastOpenedAtMs = 0;
  private silentHeartbeatTicks = 0;
  private hasReceivedPong = false;
  // The open dial whose pings were never answered; the next dial names it so the coordinator can confirm it is deaf and reset.
  private silentDialId: string | null = null;
  private closedByConsumer = false;
  private connectTimedOut = false;
  private pageHiding = false;
  private readonly buildWebsocketUrl: (
    auth: ResolvedAuthToken,
  ) => string | Promise<string>;
  private readonly resolveAuthToken: (options: {
    forceRefreshToken: boolean;
  }) => Promise<ResolvedAuthToken>;
  // (Andrew, 2026-07-29): Handlers + reconnect policy are assignable so attach() can adopt a preconnect-era connection.
  private autoReconnect: boolean;
  private onEvent?: (envelope: CoordinatorOpencodeEventEnvelope) => void;
  private onRawMessage?: (raw: unknown) => void;
  private onOpen?: RealtimeConnectionOptions["onOpen"];
  private onClose?: RealtimeConnectionOptions["onClose"];
  private onError?: (event: Event) => void;
  private onDialFailed?: RealtimeConnectionOptions["onDialFailed"];
  private onHandshakeStart?: RealtimeConnectionOptions["onHandshakeStart"];
  private lastDialTimings: RealtimeDialTimings | null = null;
  // (Andrew, 2026-07-29): Non-null only until attach(): messages that arrive while nobody is listening wait here.
  private preAttachInbox: string[] | null = null;
  private socketOptions: Options | null = null;
  private mintStartedAtMs = 0;
  private mintResolvedAtMs = 0;
  private dialId = "";
  private readonly pendingRpc = new Map<
    string,
    {
      resolve: (value: unknown) => void;
      reject: (reason: unknown) => void;
      timeoutId: ReturnType<typeof setTimeout>;
    }
  >();
  // Not PartySocket's queue, which replays a frame after its RPC was rejected.
  private readonly outboundRpcQueue: { rpcId: string; frame: string }[] = [];

  constructor({
    buildWebsocketUrl,
    resolveAuthToken,
    autoReconnect = false,
    bufferMessagesUntilAttach = false,
    onEvent,
    onRawMessage,
    onOpen,
    onClose,
    onError,
    onDialFailed,
    onHandshakeStart,
  }: RealtimeConnectionOptions & {
    buildWebsocketUrl: (auth: ResolvedAuthToken) => string | Promise<string>;
    resolveAuthToken: (options: {
      forceRefreshToken: boolean;
    }) => Promise<ResolvedAuthToken>;
    bufferMessagesUntilAttach?: boolean;
  }) {
    this.preAttachInbox = bufferMessagesUntilAttach ? [] : null;
    this.buildWebsocketUrl = buildWebsocketUrl;
    this.onHandshakeStart = onHandshakeStart;
    this.resolveAuthToken = resolveAuthToken;
    this.autoReconnect = autoReconnect;
    this.onEvent = onEvent;
    this.onRawMessage = onRawMessage;
    this.onOpen = onOpen;
    this.onClose = onClose;
    this.onError = onError;
    this.onDialFailed = onDialFailed;
    this._open();
    this.heartbeatTimer = setInterval(
      () => this._heartbeat(),
      REALTIME_HEARTBEAT_INTERVAL_MS,
    );
    this._registerWakeListeners();
  }

  // NOTE (Andrew, 2026-07-29): Adopts a preconnected connection: installs the consumer's handlers, upgrades the one-shot dial to the full reconnect policy, and replays messages that arrived while nobody was listening.
  attach(handlers: Omit<RealtimeConnectionOptions, "routing">) {
    this.onEvent = handlers.onEvent;
    this.onRawMessage = handlers.onRawMessage;
    this.onOpen = handlers.onOpen;
    this.onClose = handlers.onClose;
    this.onError = handlers.onError;
    this.onDialFailed = handlers.onDialFailed;
    this.onHandshakeStart = handlers.onHandshakeStart;
    this.autoReconnect = handlers.autoReconnect ?? true;
    if (this.autoReconnect && this.socketOptions) {
      // A one-shot socket was built with maxRetries 0; without this its drops would only recover via the 5s heartbeat, not PartySocket's backoff.
      this.socketOptions.maxRetries = Number.POSITIVE_INFINITY;
    }
    this._registerWakeListeners();
    const inbox = this.preAttachInbox ?? [];
    this.preAttachInbox = null;
    if (this.socket?.readyState === WebSocket.OPEN && this.lastDialTimings) {
      for (const data of inbox) {
        this._handleMessage(data);
      }
      this.onOpen?.(this.lastDialTimings);
      return;
    }
    // (Andrew, 2026-07-31): A still-dialing socket is the head start — keep it; its pending open listener now fires the consumer's onOpen.
    if (
      this.socket !== null &&
      (this.socket.readyState === WebSocket.CONNECTING ||
        this.socket.readyState === WebSocket.OPEN)
    ) {
      this._armConnectWatchdog(this.socket);
      return;
    }
    // Dead: replace it. Clearing `socket` first makes every listener's staleness guard fire, even on a synchronous close.
    const staleSocket = this.socket;
    this.socket = null;
    staleSocket?.close();
    this._open();
  }

  private _handleMessage(data: string) {
    const parsed = parseJsonOrNull(data);
    if (parsed === null) {
      return;
    }
    this.onRawMessage?.(parsed);

    if (isRpcResponse(parsed)) {
      const pending = this.pendingRpc.get(parsed.id);
      if (pending) {
        this.pendingRpc.delete(parsed.id);
        clearTimeout(pending.timeoutId);
        if (parsed.success) {
          pending.resolve(parsed.result);
        } else {
          pending.reject(
            new BedrockApiRequestError({
              code: REALTIME_RPC_REJECTED_CODE,
              status: 0,
              message: parsed.error ?? "rpc failed",
            }),
          );
        }
      }
      return;
    }

    if (isOpencodeEventEnvelope(parsed)) {
      this.onEvent?.(parsed);
    }
  }

  // NOTE (Andrew, 2026-06-03, REPL-27669): Backgrounded tabs throttle timers,
  // so the heartbeat can't self-heal a dead socket while away — reconnect when
  // the tab/network returns, not on reload.
  private _registerWakeListeners() {
    if (!this.autoReconnect) {
      return;
    }
    if (typeof window !== "undefined") {
      window.addEventListener("online", this._handleWake);
      window.addEventListener("focus", this._handleWake);
      window.addEventListener("pagehide", this._handlePageHide);
    }
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", this._handleVisibility);
    }
  }

  private _unregisterWakeListeners() {
    if (typeof window !== "undefined") {
      window.removeEventListener("online", this._handleWake);
      window.removeEventListener("focus", this._handleWake);
      window.removeEventListener("pagehide", this._handlePageHide);
    }
    if (typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", this._handleVisibility);
    }
  }

  // (Andrew, 2026-07-06, REPL-28639): Deliberate departures close as 4010 so server-side 1006 shrinks to genuine transport death; a bfcache restore re-dials via the wake listeners.
  private _handlePageHide = () => {
    const socket = this.socket;
    if (!socket || socket.readyState === WebSocket.CLOSED) {
      return;
    }
    this.pageHiding = true;
    socket.close(WS_CLOSE_PAGE_HIDDEN, "page hidden");
  };

  private _handleVisibility = () => {
    if (
      typeof document !== "undefined" &&
      document.visibilityState !== "visible"
    ) {
      return;
    }
    this._handleWake();
  };

  private _handleWake = () => {
    // A bfcache restore may arrive before the pagehide close event ever delivered.
    this.pageHiding = false;
    const socket = this.socket;
    if (!socket) {
      return;
    }
    if (
      socket.readyState === WebSocket.OPEN ||
      socket.readyState === WebSocket.CONNECTING
    ) {
      return;
    }
    socket.reconnect();
  };

  ping() {
    this._send({ type: "ping" });
  }

  /** Fire-and-forget a JSON frame; PartySocket buffers it if the dial is still in flight. */
  send(message: unknown) {
    this._send(message);
  }

  // NOTE (Andrew, 2026-06-03, REPL-27669):
  // Chaos/QA: simulate the real production failure (dropped transport / network
  // blip) so the autoReconnect path self-heals it. Closes the live transport
  // directly — PartySocket.close() would set _shouldReconnect=false and wedge it.
  debugDropConnection() {
    // eslint-disable-next-line replo/no-as-cast -- PartySocket's live `_ws` is private with no accessor; closing it directly (vs PartySocket.close, which wedges) is the only way to exercise the auto-reconnect path
    const internals = this.socket as unknown as { _ws?: WebSocket } | null;
    internals?._ws?.close();
  }

  // NOTE (Andrew, 2026-06-03, REPL-27669): Force an immediate reconnect,
  // dropping a dead transport. Backs the debug panel's Reconnect control.
  reconnect() {
    this.socket?.reconnect();
  }

  private _heartbeat() {
    const socket = this.socket;
    if (!socket) {
      return;
    }
    if (socket.readyState !== WebSocket.OPEN) {
      // NOTE (Andrew, 2026-06-03, REPL-27669): Down — PartySocket's retry can
      // stall or crawl behind a grown backoff. Force a fresh dial (resets the
      // backoff) so a foreground tab self-heals without a focus event; skip
      // CONNECTING to not abort an in-flight dial.
      if (this.autoReconnect && socket.readyState !== WebSocket.CONNECTING) {
        socket.reconnect();
      }
      return;
    }
    this.silentHeartbeatTicks += 1;
    if (this.silentHeartbeatTicks <= REALTIME_HEARTBEAT_MAX_SILENT_TICKS) {
      this._send({ type: "ping" });
      return;
    }
    this.silentHeartbeatTicks = 0;
    if (!this.hasReceivedPong) {
      this.silentDialId = this.lastDialTimings?.dialId ?? null;
    }
    // (Andrew, 2026-07-01, REPL-28678): 4008 splits watchdog give-ups from transport-death 1006s; reconnect() re-dials where close() would wedge (_shouldReconnect=false).
    if (this.autoReconnect) {
      socket.reconnect(WS_CLOSE_HEARTBEAT_TIMEOUT, "heartbeat timeout");
    } else {
      socket.close(WS_CLOSE_HEARTBEAT_TIMEOUT, "heartbeat timeout");
    }
  }

  // (Andrew, 2026-07-31): One-shot dials skip the watchdog; adoption arms it when it lifts the reconnect policy.
  private _armConnectWatchdog(socket: InstanceType<typeof PartySocket>) {
    if (!this.autoReconnect) {
      return;
    }
    this.connectionTimeoutId = setTimeout(() => {
      if (socket.readyState === WebSocket.CONNECTING) {
        this.connectTimedOut = true;
        socket.close();
      }
    }, REALTIME_CONNECTION_TIMEOUT_MS);
  }

  private clearConnectionTimeout() {
    if (!this.connectionTimeoutId) {
      return;
    }
    clearTimeout(this.connectionTimeoutId);
    this.connectionTimeoutId = null;
  }

  callMethod<ResponseType = unknown>(
    method: string,
    args: unknown[] = [],
    options: { timeoutMs?: number } = {},
  ): Promise<ResponseType> {
    const id = uuidv4();
    // Callers with slower server work may stretch this; every RPC stays bounded.
    const timeoutMs = options.timeoutMs ?? REALTIME_RPC_TIMEOUT_MS;

    return new Promise<ResponseType>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        this.pendingRpc.delete(id);
        this._dropQueuedRpc(id);
        reject(
          new BedrockApiRequestError({
            code: REALTIME_RPC_TIMEOUT_CODE,
            status: 0,
            message: `RPC '${method}' timed out after ${timeoutMs}ms`,
          }),
        );
      }, timeoutMs);
      this.pendingRpc.set(id, {
        // Legacy cast - may or may not be necessary
        // eslint-disable-next-line replo/no-as-cast
        resolve: (value) => resolve(value as ResponseType),
        reject,
        timeoutId,
      });
      this._sendRpcFrame({
        rpcId: id,
        frame: JSON.stringify({ type: "rpc", id, method, args }),
      });
    });
  }

  // Written only to an open socket; otherwise it waits for the next open, and
  // only while its RPC is still pending.
  private _sendRpcFrame(queuedRpc: { rpcId: string; frame: string }) {
    if (this.socket?.readyState !== WebSocket.OPEN) {
      this.outboundRpcQueue.push(queuedRpc);
      return;
    }
    this.socket.send(queuedRpc.frame);
  }

  private _dropQueuedRpc(rpcId: string) {
    const index = this.outboundRpcQueue.findIndex(
      (queuedRpc) => queuedRpc.rpcId === rpcId,
    );
    if (index !== -1) {
      this.outboundRpcQueue.splice(index, 1);
    }
  }

  close() {
    this.closedByConsumer = true;
    this._unregisterWakeListeners();
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
    }
    this.clearConnectionTimeout();
    // Consumer teardown mid-RPC is a transport outcome, not a server "no": tag it
    // with the transport-closed code (so isRealtimeTransportError catches it and
    // callers/handlers can treat it like a network blip) and don't claim the
    // socket never opened — this fires whether or not it had.
    this._failPending({
      reason: new BedrockApiRequestError({
        code: REALTIME_CONNECTION_CLOSED_CODE,
        status: 0,
        message: REALTIME_CONNECTION_CLOSED_MESSAGE,
      }),
      keepQueued: false,
    });
    try {
      this.socket?.close();
    } catch {
      // socket may already be closed; nothing to do
    }
  }

  // NOTE (Andrew, 2026-06-03, REPL-27669): Halt every path that would re-dial —
  // PartySocket's own retries, the heartbeat watchdog, and the focus/online wake
  // listeners — so a terminal close (e.g. revoked auth) stays closed instead of
  // looping.
  private _stopReconnecting() {
    this._unregisterWakeListeners();
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    try {
      this.socket?.close();
    } catch {
      // socket may already be closed; nothing to do
    }
  }

  private _open() {
    this.closedByConsumer = false;
    const minReconnectionDelayWithJitter =
      REALTIME_BASE_RECONNECT_DELAY_MS +
      Math.random() * REALTIME_BASE_RECONNECT_DELAY_MS;
    // (Andrew, 2026-07-29): Held by reference — PartySocket re-reads maxRetries on every _connect(), which is how attach() lifts a one-shot's retry cap.
    const socketOptions: Options = {
      connectionTimeout: REALTIME_CONNECTION_TIMEOUT_MS,
      maxEnqueuedMessages: 1_000,
      maxReconnectionDelay: REALTIME_MAX_RECONNECT_DELAY_MS,
      // NOTE (Andrew, 2026-06-03, REPL-27669): Retry transient drops
      // indefinitely; backoff caps the rate, auth is bounded separately.
      maxRetries: this.autoReconnect ? Number.POSITIVE_INFINITY : 0,
      minReconnectionDelay: minReconnectionDelayWithJitter,
      minUptime: REALTIME_STABLE_CONNECTION_MS,
      reconnectionDelayGrowFactor: 2,
    };
    this.socketOptions = socketOptions;
    const socket = new PartySocket(
      () => this.getWebsocketUrl(),
      () => this.getWebsocketProtocols(),
      socketOptions,
    );
    this.socket = socket;
    this._armConnectWatchdog(socket);
    socket.addEventListener("open", () => {
      // (Andrew, 2026-07-29): attach() can replace the socket; a superseded socket's late events must not reach the handlers.
      if (this.socket !== socket) {
        return;
      }
      this.clearConnectionTimeout();
      this.lastOpenedAtMs = Date.now();
      const queuedRpcs = this.outboundRpcQueue.splice(0);
      for (const { rpcId, frame } of queuedRpcs) {
        if (this.pendingRpc.has(rpcId)) {
          this._sendRpcFrame({ rpcId, frame });
        }
      }
      this.lastDialTimings = {
        tokenMintMs: this.mintResolvedAtMs - this.mintStartedAtMs,
        wsHandshakeMs: this.lastOpenedAtMs - this.mintResolvedAtMs,
        dialId: this.dialId,
      };
      this.onOpen?.(this.lastDialTimings);
      this.silentHeartbeatTicks = 0;
      // This dial's URL already carried any report; a fresh socket starts unproven.
      this.silentDialId = null;
      this.hasReceivedPong = false;
    });
    socket.addEventListener(
      "message",
      (event: WebSocketEventMap["message"]) => {
        if (this.socket !== socket) {
          return;
        }
        this.silentHeartbeatTicks = 0;
        if (event.data === PONG_FRAME) {
          this.hasReceivedPong = true;
        }
        if (typeof event.data !== "string") {
          return;
        }
        if (this.preAttachInbox) {
          this.preAttachInbox.push(event.data);
          if (this.preAttachInbox.length > PRE_ATTACH_INBOX_LIMIT) {
            this.preAttachInbox.shift();
          }
          return;
        }
        this._handleMessage(event.data);
      },
    );
    socket.addEventListener("close", (event: WebSocketEventMap["close"]) => {
      if (this.socket !== socket) {
        return;
      }
      this.clearConnectionTimeout();
      // A deferred 4001 fires `open` first, so only forgive the auth budget if this socket actually stayed up (REPL-28639).
      const openedAtMs = this.lastOpenedAtMs;
      this.lastOpenedAtMs = 0;
      if (
        openedAtMs !== 0 &&
        Date.now() - openedAtMs >= REALTIME_STABLE_CONNECTION_MS
      ) {
        this.authFailureCount = 0;
      }
      if (event.code === WS_CLOSE_AUTH_FAILED) {
        this.forceRefreshTokenOnNextConnect = true;
        this.authFailureCount += 1;
      }
      // (Andrew, 2026-07-05, REPL-28639): A close on a never-opened socket is a failed dial, not a drop — surface it separately; consumer-initiated teardown mid-dial doesn't count.
      if (openedAtMs === 0 && !this.closedByConsumer && !this.pageHiding) {
        this.onDialFailed?.({
          reason: this.connectTimedOut
            ? "connect-timeout"
            : "socket-error-before-open",
          message:
            `close code=${event.code}${event.reason ? ` reason=${event.reason}` : ""}`.slice(
              0,
              DIAL_FAILURE_MESSAGE_MAX_LENGTH,
            ),
        });
      }
      this.connectTimedOut = false;
      this.pageHiding = false;
      // NOTE (Andrew, 2026-06-03, REPL-27669): Transient drops reconnect
      // forever; only a repeatedly-rejected token is terminal, so the consumer
      // can surface an irrecoverable disconnect.
      // (Andrew, 2026-07-01, REPL-28688): A 4002 identity mismatch is terminal on the first close — a refreshed token has the same sub, so no retry can succeed.
      const willReconnect =
        this.autoReconnect &&
        event.code !== WS_CLOSE_IDENTITY_MISMATCH &&
        this.authFailureCount <= REALTIME_MAX_AUTH_RETRIES;
      this._failPending({
        reason: new BedrockApiRequestError({
          code: REALTIME_CONNECTION_CLOSED_CODE,
          status: 0,
          message: REALTIME_CONNECTION_CLOSED_MESSAGE,
        }),
        keepQueued: willReconnect,
      });
      if (this.autoReconnect && !willReconnect) {
        this._stopReconnecting();
      }
      this.onClose?.(event, { willReconnect });
    });
    socket.addEventListener("error", (event: WebSocketEventMap["error"]) => {
      if (this.socket !== socket) {
        return;
      }
      this.onError?.(event);
    });
  }

  private _send(message: unknown) {
    if (!this.socket) {
      return;
    }
    this.socket.send(JSON.stringify(message));
  }

  // A queued RPC never reached the server, so a drop that will redial leaves it for the next open, still bounded by its own timeout.
  private _failPending({
    reason,
    keepQueued,
  }: {
    reason: unknown;
    keepQueued: boolean;
  }) {
    const queuedRpcIds = new Set(
      keepQueued ? this.outboundRpcQueue.map(({ rpcId }) => rpcId) : [],
    );
    for (const [rpcId, pending] of this.pendingRpc) {
      if (queuedRpcIds.has(rpcId)) {
        continue;
      }
      clearTimeout(pending.timeoutId);
      pending.reject(reason);
      this.pendingRpc.delete(rpcId);
    }
    if (!keepQueued) {
      this.outboundRpcQueue.length = 0;
    }
  }

  private getConnectionAuth() {
    if (!this.connectionAuthPromise) {
      const forceRefreshToken = this.forceRefreshTokenOnNextConnect;
      this.forceRefreshTokenOnNextConnect = false;
      // This promise is minted once per dial attempt and cleared when the attempt settles, so it alone bounds the mint.
      this.mintStartedAtMs = Date.now();
      this.dialId = uuidv4();
      this.connectionAuthPromise = this.resolveAuthToken({
        forceRefreshToken,
      }).then((auth) => {
        if (!auth.token) {
          throw new BedrockApiRequestError({
            code: "UNAUTHORIZED",
            status: 0,
            message:
              "Realtime connections require authenticated Bedrock clients",
          });
        }
        this.mintResolvedAtMs = Date.now();
        // NOTE (Andrew, 2026-07-28): This runs inside the promise that gates the dial, so a throwing observer would fail it.
        try {
          this.onHandshakeStart?.(this.dialId);
        } catch {}
        return auth;
      });
    }
    return this.connectionAuthPromise;
  }

  private async getWebsocketUrl() {
    let auth: ResolvedAuthToken;
    try {
      auth = await this.getConnectionAuth();
    } catch (error) {
      this._failDial({ error, reason: "token-mint-failed" });
      throw error;
    }
    try {
      const url = new URL(await this.buildWebsocketUrl(auth));
      // NOTE (Andrew, 2026-07-28): The DO name comes from the path, so this param joins the server's log without touching routing.
      url.searchParams.set("dialId", this.dialId);
      if (this.silentDialId) {
        url.searchParams.set("silentDialId", this.silentDialId);
      }
      return url.toString();
    } catch (error) {
      this._failDial({ error, reason: "url-build-failed" });
      throw error;
    }
  }

  private _failDial({
    error,
    reason,
  }: {
    error: unknown;
    reason: RealtimeDialFailureReason;
  }) {
    this.connectionAuthPromise = null;
    this.forceRefreshTokenOnNextConnect = true;
    this._failPending({
      reason: error,
      keepQueued:
        this.autoReconnect && isRetryableDialFailure({ error, reason }),
    });
    this.onDialFailed?.({
      reason,
      message: (error instanceof Error ? error.message : String(error)).slice(
        0,
        DIAL_FAILURE_MESSAGE_MAX_LENGTH,
      ),
    });
    // NOTE (Andrew, 2026-06-03, REPL-27669): Token resolution failing
    // (offline, Airlock blip) is transient — a reconnecting client keeps
    // retrying. A one-shot client gives up now.
    if (!this.autoReconnect) {
      this.onClose?.(undefined, { willReconnect: false });
    }
  }

  private async getWebsocketProtocols() {
    try {
      const auth = await this.getConnectionAuth();
      return [
        REPLO_BEDROCK_WS_PROTOCOL,
        `${REPLO_BEARER_WS_PROTOCOL_PREFIX}${auth.token}`,
      ];
    } finally {
      this.connectionAuthPromise = null;
    }
  }
}

// A mint that fails in transit (Airlock blip, offline) heals on a later dial; a missing token, a signed-out session or an unbuildable URL fails every dial the same way.
function isRetryableDialFailure({
  error,
  reason,
}: {
  error: unknown;
  reason: RealtimeDialFailureReason;
}): boolean {
  return (
    reason === "token-mint-failed" &&
    !(error instanceof BedrockSessionInvalidatedError) &&
    !(error instanceof BedrockApiRequestError && error.code === "UNAUTHORIZED")
  );
}

function isRpcResponse(
  value: unknown,
): value is { type: "rpc"; id: string } & (
  | { success: true; result: unknown }
  | { success: false; error?: string }
) {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  // Legacy cast - may or may not be necessary
  // eslint-disable-next-line replo/no-as-cast
  const record = value as Record<string, unknown>;
  return (
    record.type === "rpc" &&
    typeof record.id === "string" &&
    typeof record.success === "boolean"
  );
}

function isOpencodeEventEnvelope(
  value: unknown,
): value is CoordinatorOpencodeEventEnvelope {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  // Narrowing `value` (typed `unknown`) to a generic index-signature so we can
  // safely probe the discriminator fields below; the explicit null/object guard
  // above makes this structurally sound.
  // eslint-disable-next-line replo/no-as-cast
  const record = value as Record<string, unknown>;
  return (
    record.type === "opencode-event" &&
    typeof record.payload === "object" &&
    record.payload !== null
  );
}
